import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import UserSelect from 'components/form/UserSelect';
import { UserResult } from 'stores/UserStoreAdapter';

export type ConfirmDialogType = 'create' | 'change' | 'terminate';

type ContractConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: ConfirmDialogResult) => void;
  type: ConfirmDialogType;
  /** MANAGER権限ユーザー一覧（申請者候補） */
  managers: UserResult[];
  /** BACK_OFFICE権限ユーザー一覧（申請先候補） */
  backOfficeUsers: UserResult[];
  /** ログイン中ユーザーID（申請者のデフォルト選択用） */
  currentUserId: string;
  /** 新規申請の場合の契約開始日（読み取り専用表示） */
  contractStartDate?: string;
  /** 解除申請の場合の契約終了日（読み取り専用表示） */
  contractEndDate?: string;
};

export type ConfirmDialogResult = {
  applicantUserIds: string[];
  destinationUserIds: string[];
  validStartDate?: string;
};

const DIALOG_TITLES: Record<ConfirmDialogType, string> = {
  create: '契約申請を申請しますか？',
  change: '契約申請を申請しますか？',
  terminate: '契約を終了しますか？',
};

const ContractConfirmDialog: React.FC<ContractConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  type,
  managers,
  backOfficeUsers,
  currentUserId,
  contractStartDate,
  contractEndDate,
}) => {
  const [applicantUserIds, setApplicantUserIds] = useState<string[]>([]);
  const [destinationUserIds, setDestinationUserIds] = useState<string[]>([]);
  const [validStartDate, setValidStartDate] = useState('');

  useEffect(() => {
    if (open) {
      setApplicantUserIds([currentUserId]);
      setDestinationUserIds([]);
      setValidStartDate('');
    }
  }, [open, currentUserId]);

  const isValid = () => {
    if (applicantUserIds.length === 0) return false;
    if (destinationUserIds.length === 0) return false;
    if (type === 'change' && !validStartDate) return false;
    return true;
  };

  const handleConfirm = () => {
    onConfirm({
      applicantUserIds,
      destinationUserIds,
      validStartDate: type === 'change' ? validStartDate : undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {DIALOG_TITLES[type]}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* 解除申請: 契約終了日表示 */}
          {type === 'terminate' && contractEndDate && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                契約終了日
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {contractEndDate.substring(0, 10).replace(/-/g, '/')}
              </Typography>
            </Box>
          )}

          {/* 申請者 */}
          <UserSelect
            mode="multi"
            users={managers}
            selectedUserIds={applicantUserIds}
            onChange={setApplicantUserIds}
            label="申請者"
            required
          />

          {/* 申請先 */}
          <UserSelect
            mode="multi"
            users={backOfficeUsers}
            selectedUserIds={destinationUserIds}
            onChange={setDestinationUserIds}
            label="申請先"
            required
          />

          {/* 新規申請: 有効開始日（読み取り専用） */}
          {type === 'create' && contractStartDate && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                有効開始日
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {contractStartDate.substring(0, 10).replace(/-/g, '/')}
              </Typography>
            </Box>
          )}

          {/* 変更申請: 有効開始日（入力） */}
          {type === 'change' && (
            <FinancialYearMonthSelect
              dateType="start"
              value={validStartDate}
              onChange={setValidStartDate}
              label="有効開始日"
              required
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={!isValid()}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          申請
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractConfirmDialog;
